import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import AppSheetMrtTable from './AppSheetMrtTable';

const DEFAULT_PAGE_SIZE = 100;

const MetricCard = ({ label, value }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </CardContent>
  </Card>
);

const buildColumns = (fields = []) => {
  const base = [
    { id: '__reportProgram', header: 'Program', size: 160, accessorFn: (row) => row.__reportProgram },
    { id: '__orderItemId', header: 'OrderItem Id', size: 180, accessorFn: (row) => row.__orderItemId },
    { id: '__appsheetProgram', header: 'AppSheet Program', size: 180, accessorFn: (row) => row.__appsheetProgram },
    { id: '__dpeProgram', header: 'DPE Program', size: 160, accessorFn: (row) => row.__dpeProgram },
  ];
  const extra = fields.map((field) => ({
    id: field.name,
    header: field.displayName || field.name,
    size: 180,
    accessorFn: (row) => row?.[field.name],
  }));
  return [...base, ...extra];
};

const AppSheetUnmatchedProducts = () => {
  const [programName, setProgramName] = useState('All Programs');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [excludeCancelled, setExcludeCancelled] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ['appsheet-unmatched-products', programName, pagination, excludeCancelled],
    queryFn: async () => {
      const params = {
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
        excludeCancelled: excludeCancelled ? 'true' : undefined,
      };
      if (programName && programName !== 'All Programs') {
        params.programName = programName;
      }
      const response = await axios.get('/api/appsheet/reports/unmatched-products', { params });
      return response.data;
    },
    keepPreviousData: true,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, err) => {
      const status = err?.response?.status;
      if (status === 429) return false;
      return failureCount < 2;
    },
  });

  const columns = useMemo(() => buildColumns(data?.table?.fields || []), [data?.table?.fields]);
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const total = data?.total ?? 0;
  const programs = useMemo(() => (Array.isArray(data?.programs) ? data.programs : []), [data?.programs]);
  const summary = data?.summary || {};
  const totalOrderItems = summary.totalOrderItems ?? 0;
  const filteredNegativeQuantity = summary.filteredNegativeQuantity ?? 0;
  const filteredCancelledNoProgram = summary.filteredCancelledNoProgram ?? 0;
  const matchedCount = summary.matchedCount ?? 0;

  useEffect(() => {
    if (programName === 'All Programs') return;
    const exists = programs.some((p) => p.programName === programName);
    if (!exists && programs.length > 0) {
      setProgramName('All Programs');
    }
  }, [programName, programs]);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h6">Unmatched Products (AppSheet vs DPE)</Typography>
        <FormControl sx={{ minWidth: 220 }} size="small">
          <InputLabel>Program</InputLabel>
          <Select
            value={programName}
            label="Program"
            onChange={(event) => {
              setProgramName(event.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          >
            <MenuItem value="All Programs">All Programs</MenuItem>
            {programs.map((program) => (
              <MenuItem key={program.programName} value={program.programName}>
                {program.programName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.response?.data?.message || error?.message || 'Failed to load unmatched products report.'}
        </Alert>
      )}

      {!isLoading && !error && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4} md={3}>
              <MetricCard label="Total Unmatched" value={total} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <MetricCard label="Matched in DPE" value={matchedCount} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <MetricCard label="Total OrderItems" value={totalOrderItems} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <MetricCard label="Filtered (qty < 0)" value={filteredNegativeQuantity} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <MetricCard label="Filtered (cancelled, no program)" value={filteredCancelledNoProgram} />
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Unmatched by Program
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {programs.length === 0 && <Typography variant="body2">No unmatched products found.</Typography>}
                    {programs.map((program) => (
                      <Chip key={program.programName} label={`${program.programName}: ${program.count}`} />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <AppSheetMrtTable
            rows={rows}
            columns={columns}
            rowCount={total}
            isLoading={isLoading}
            pagination={pagination}
            onPaginationChange={setPagination}
            title="Unmatched Products (AppSheet vs DPE)"
            toolbarActions={
              <Chip
                label="Remove Cancelled"
                color={excludeCancelled ? 'primary' : 'default'}
                variant={excludeCancelled ? 'filled' : 'outlined'}
                onClick={() => {
                  setExcludeCancelled((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
              />
            }
          />
        </>
      )}
    </Box>
  );
};

export default AppSheetUnmatchedProducts;
